import { ServicePortfolioType } from '../../enums/ServicePortfolioType';
import { SortDirection } from '../../enums/SortDirection';
import { NoteResource } from '../../types/Note';
import { ServiceCustomerProfileModalResource } from '../../types/service-portfolio/ServiceCustomerProfileModalResource';
import {
  appointmentsDefaultDateRangeValue,
  appointmentsDefaultFromDate,
  appointmentsDefaultToDate,
  appointmentsMaxDate,
  appointmentsMinDate,
  recentDefaultFromDate,
  recentDefaultToDate,
  recentMaxDate,
  recentMinDate,
  recentsDefaultDateRangeValue,
  ServiceFilterResource,
  ServiceSelectedFilterResource,
} from '../../types/service-portfolio/ServiceFilterResource';
import { ServicePortfolioResource } from '../../types/service-portfolio/ServicePortfolioResource';
import { ServiceView } from '../../types/service-portfolio/ServiceView';
import { ServiceActions, ServiceActionsType } from './ServiceActions';

export type ServiceState = {
  driveView: ServiceView;
  appointmentView: ServiceView;
  recentView: ServiceView;
  isNotesModalOpen: boolean;
  notesModalResource: NoteResource;
  isCustomerProfileModalOpen: boolean;
  customerProfileModalResource: ServiceCustomerProfileModalResource;
  isClientActivationModalOpen: boolean;
  isClientActivatedSuccessModalOpen: boolean;
  isLoading: boolean;
  isServiceFilterReset: boolean;
  openFilterCount: number;
  selectedPortfolioType: ServicePortfolioType;
  selectedPortfolioTotalRecord: number;
  isServiceOnly: boolean;
  isClosedDealOnly: boolean;
  isFiltersShowing: boolean;
  isToggleUpdate: string;
  toasterText: string;
  isAddFreshUpOpen: boolean;
  rightMobileLayout: boolean;
  selectedPortfolioRecord: ServicePortfolioResource;
  bulkSelectionRecords: ServicePortfolioResource[];
  toggleAllRowsSelection: boolean;
  bulkSelectionRefreshCount: number;
  isAllRowsSelected: boolean;
};

const initialFilters: ServiceFilterResource = {
  maxBps: null,
  minBps: null,
  years: [],
  makes: [],
  models: [],
  trims: [],
  salesPersons: [],
  serviceAdvisors: [],
  roStatuses: [],
  interactionStatuses: [],
  dealTypes: [],
  emailValidationStatus: [],
  optOutPreference: [],
};

const initialSelectedFilters: ServiceSelectedFilterResource = {
  bpsValue: null,
  years: [],
  makes: [],
  models: [],
  trims: [],
  isTrimConfirmationRequired: null,
  salesPersons: [],
  serviceAdvisors: [],
  roStatuses: [],
  interactionStatuses: [],
  dealTypes: [],
  dateRange: null,
  emailValidationStatus: null,
  optOutPreference: [],
};

export const initialServicePortfolioState: ServiceState = {
  driveView: {
    serviceRequest: null,
    shouldRefresh: false,
    notesCountMap: [],
    filters: initialFilters,
    selectedFilters: initialSelectedFilters,
    isFilterChanged: false,
    masterFilterKey: null,
    totalRecordsCount: 0,
    minDate: null,
    maxDate: null,
    defaultFromDate: null,
    defaultToDate: null,
    sort: {
      accessor: 'status',
      sortDirection: SortDirection.Descending,
      subSort: 'openedTime',
      subSortDirection: SortDirection.Descending,
    },
    pageSize: 10,
    currentPage: 1,
  },
  appointmentView: {
    serviceRequest: null,
    shouldRefresh: false,
    notesCountMap: [],
    filters: initialFilters,
    selectedFilters: { ...initialSelectedFilters, dateRange: appointmentsDefaultDateRangeValue },
    isFilterChanged: false,
    masterFilterKey: null,
    totalRecordsCount: 0,
    minDate: appointmentsMinDate,
    maxDate: appointmentsMaxDate,
    defaultFromDate: appointmentsDefaultFromDate,
    defaultToDate: appointmentsDefaultToDate,
    sort: { accessor: 'arrivedDate', sortDirection: SortDirection.Ascending },
    pageSize: 10,
    currentPage: 1,
  },
  recentView: {
    serviceRequest: null,
    shouldRefresh: false,
    notesCountMap: [],
    filters: initialFilters,
    selectedFilters: { ...initialSelectedFilters, dateRange: recentsDefaultDateRangeValue },
    isFilterChanged: false,
    masterFilterKey: null,
    totalRecordsCount: 0,
    minDate: recentMinDate,
    maxDate: recentMaxDate,
    defaultFromDate: recentDefaultFromDate,
    defaultToDate: recentDefaultToDate,
    sort: {
      accessor: 'arrivedDate',
      sortDirection: SortDirection.Descending,
      subSort: 'roTotalAmount',
      subSortDirection: SortDirection.Descending,
    },
    pageSize: 10,
    currentPage: 1,
  },
  isNotesModalOpen: false,
  isCustomerProfileModalOpen: false,
  isClientActivationModalOpen: false,
  isClientActivatedSuccessModalOpen: false,
  notesModalResource: {
    dealerId: '',
    fullName: '',
    closedDealId: '',
    vin: '',
    status: '',
    subStatus: '',
  },
  customerProfileModalResource: {
    customer: null,
    brandId: 0,
    model: '',
    trim: '',
    year: 0,
    vin: '',
    dealerId: '',
    miles: '',
    isTrimConfirmationRequired: false,
    customerNumber: '',
  },
  isLoading: false,
  isServiceFilterReset: false,
  openFilterCount: 0,
  selectedPortfolioType: null,
  selectedPortfolioTotalRecord: null,
  isServiceOnly: false,
  isClosedDealOnly: false,
  isFiltersShowing: false,
  isToggleUpdate: '',
  toasterText: '',
  isAddFreshUpOpen: false,
  rightMobileLayout: false,
  selectedPortfolioRecord: null,
  bulkSelectionRecords: [],
  toggleAllRowsSelection: false,
  bulkSelectionRefreshCount: 0,
  isAllRowsSelected: false,
};

export const serviceReducer = (
  state: ServiceState = initialServicePortfolioState,
  action: ServiceActions,
): ServiceState => {
  switch (action.type) {
    case ServiceActionsType.SET_SERVICE_REQUEST_RESOURCE: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              serviceRequest: action.payload.requestBody,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              serviceRequest: action.payload.requestBody,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              serviceRequest: action.payload.requestBody,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              serviceRequest: action.payload.requestBody,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_SELECTED_SORT: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              sort: action.payload.requestBody,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              sort: action.payload.requestBody,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              sort: action.payload.requestBody,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              sort: action.payload.requestBody,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_SELECTED_PAGE_SIZE: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              pageSize: action.payload.pageSize,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              pageSize: action.payload.pageSize,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              pageSize: action.payload.pageSize,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              pageSize: action.payload.pageSize,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_SELECTED_CURRENT_PAGE: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              currentPage: action.payload.currentPage,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              currentPage: action.payload.currentPage,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              currentPage: action.payload.currentPage,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              currentPage: action.payload.currentPage,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_DATA_TOTAL_COUNT: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              totalRecordsCount: action.payload.value,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              totalRecordsCount: action.payload.value,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              totalRecordsCount: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              totalRecordsCount: action.payload.value,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_NOTES_MODAL_OPEN: {
      return {
        ...state,
        isNotesModalOpen: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_IS_SERVICE_ONLY: {
      return {
        ...state,
        isServiceOnly: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_IS_CLOSED_DEAL_ONLY: {
      return {
        ...state,
        isClosedDealOnly: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_NOTES_MODAL_RESOURCE: {
      return {
        ...state,
        notesModalResource: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_CUSTOMER_PROFILE_MODAL_RESOURCE: {
      return {
        ...state,
        customerProfileModalResource: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_CUSTOMER_PROFILE_MODAL_OPEN:
      return {
        ...state,
        isCustomerProfileModalOpen: action.payload,
      };

    case ServiceActionsType.SET_SERVICE_ADD_FRESH_UP_MODAL_OPEN:
      return {
        ...state,
        isAddFreshUpOpen: action.payload,
      };

    case ServiceActionsType.SET_SERVICE_CLIENT_ACTIVATION_MODAL_OPEN:
      return {
        ...state,
        isClientActivationModalOpen: action.payload,
      };

    case ServiceActionsType.SET_SERVICE_CLIENT_ACTIVATED_SUCCESS_MODAL_OPEN:
      return {
        ...state,
        isClientActivatedSuccessModalOpen: action.payload,
      };

    case ServiceActionsType.SET_SERVICE_PORTFOLIO_IS_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ServiceActionsType.SET_IS_SERVICE_FILTER_RESET: {
      return {
        ...state,
        isServiceFilterReset: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_FILTERS: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              filters: action.payload.filters,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              filters: action.payload.filters,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              filters: action.payload.filters,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              filters: action.payload.filters,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_SELECTED_FILTERS: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              selectedFilters: action.payload.filters,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              selectedFilters: action.payload.filters,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              selectedFilters: action.payload.filters,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              selectedFilters: action.payload.filters,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_IS_SERVICE_FILTERS_CHANGED: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              isFilterChanged: action.payload.value,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              isFilterChanged: action.payload.value,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              isFilterChanged: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              isFilterChanged: action.payload.value,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_MASTER_FILTER: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              masterFilterKey: action.payload.key,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              masterFilterKey: action.payload.key,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              masterFilterKey: action.payload.key,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              masterFilterKey: action.payload.key,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_OPEN_FILTER_COUNT: {
      return {
        ...state,
        openFilterCount: action.payload,
      };
    }

    case ServiceActionsType.SET_IS_FILTERS_SHOWING: {
      return {
        ...state,
        isFiltersShowing: action.payload,
      };
    }

    case ServiceActionsType.SET_SELECTED_PORTFOLIO_TYPE: {
      return {
        ...state,
        selectedPortfolioType: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_UPDATED_NOTES_COUNT: {
      const dealType = action.payload.dealType;

      switch (dealType) {
        case ServicePortfolioType.Drive:
          const currentLeaseNotesCount = state.driveView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentLeaseNotesCount.count = action.payload.value.count;

          return {
            ...state,
            driveView: {
              ...state.driveView,
              notesCountMap: state.driveView.notesCountMap,
            },
          };

        case ServicePortfolioType.Appointments:
          const currentFinanceNotesCount = state.appointmentView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentFinanceNotesCount.count = action.payload.value.count;
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              notesCountMap: state.appointmentView.notesCountMap,
            },
          };

        case ServicePortfolioType.RecentService:
          const currentCashNotesCount = state.recentView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentCashNotesCount.count = action.payload.value.count;
          return {
            ...state,
            recentView: {
              ...state.recentView,
              notesCountMap: state.recentView.notesCountMap,
            },
          };

        default: {
          const currentDefaultNotesCount = state.driveView.notesCountMap?.find(
            (x) => x.leadId === action.payload.value.leadId,
          );
          currentDefaultNotesCount.count = action.payload.value.count;
          return {
            ...state,
            driveView: {
              ...state.driveView,
              notesCountMap: state.driveView.notesCountMap,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_NOTES_COUNT_MAP: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              notesCountMap: action.payload.value,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              notesCountMap: action.payload.value,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              notesCountMap: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              notesCountMap: action.payload.value,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SERVICE_SHOULD_REFRESH: {
      const dealType = action.payload.dealType;
      switch (dealType) {
        case ServicePortfolioType.Drive:
          return {
            ...state,
            driveView: {
              ...state.driveView,
              shouldRefresh: action.payload.value,
            },
          };

        case ServicePortfolioType.Appointments:
          return {
            ...state,
            appointmentView: {
              ...state.appointmentView,
              shouldRefresh: action.payload.value,
            },
          };

        case ServicePortfolioType.RecentService:
          return {
            ...state,
            recentView: {
              ...state.recentView,
              shouldRefresh: action.payload.value,
            },
          };

        default: {
          return {
            ...state,
            driveView: {
              ...state.driveView,
              shouldRefresh: action.payload.value,
            },
          };
        }
      }
    }

    case ServiceActionsType.SET_SELECTED_SERVICE_PORTFOLIO_TOTAL_RECORD: {
      return {
        ...state,
        selectedPortfolioTotalRecord: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_TOGGLE_UPDATE: {
      return {
        ...state,
        isToggleUpdate: action.payload,
      };
    }

    case ServiceActionsType.SET_SERVICE_TOASTER_TEXT: {
      return {
        ...state,
        toasterText: action.payload,
      };
    }

    case ServiceActionsType.SET_RIGHT_MOBILE_LAYOUT: {
      return {
        ...state,
        rightMobileLayout: action.payload,
      };
    }

    case ServiceActionsType.SET_SELECTED_SERVICE_PORTFOLIO_RECORD: {
      return {
        ...state,
        selectedPortfolioRecord: action.payload,
      };
    }

    case ServiceActionsType.SET_BULK_SELECTED_REDORDS: {
      return {
        ...state,
        bulkSelectionRecords: action.payload,
      };
    }

    case ServiceActionsType.SET_TOGGLE_ALL_ROWS_SELECTION: {
      return {
        ...state,
        toggleAllRowsSelection: action.payload,
      };
    }

    case ServiceActionsType.SET_BULK_SELECTION_RESFRESH_COUNT: {
      return {
        ...state,
        bulkSelectionRefreshCount: action.payload,
      };
    }

    case ServiceActionsType.SET_IS_ALL_ROWS_SELECTED: {
      return {
        ...state,
        isAllRowsSelected: action.payload,
      };
    }

    default:
      return state;
  }
};
